import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const supportsHover = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
};

// Flat list item — no nested maps, no dynamic icon rendering
function DropdownLink({ to, label, IconComp, badge, highlight, isOpen, onClose, testId }) {
  return (
    <Link
      to={to}
      className={highlight
        ? 'flex items-center gap-2.5 px-3 py-2 text-sm text-primary bg-primary/5 hover:bg-primary/10 transition-colors'
        : 'flex items-center gap-2.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors'
      }
      onClick={onClose}
      data-testid={testId}
      role="menuitem"
      tabIndex={isOpen ? 0 : -1}
    >
      {IconComp}
      <span className="flex-1 truncate">{label}</span>
      {badge && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-stable/20 text-stable font-semibold uppercase tracking-wide flex-shrink-0">
          {badge}
        </span>
      )}
    </Link>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="px-3 pt-2.5 pb-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
        {title}
      </span>
    </div>
  );
}

function SectionDivider() {
  return <div className="mx-3 my-0.5 border-t border-border/50" />;
}

export function NavDropdown({ label, icon: Icon, renderContent, isActive, testId, customTrigger }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const closeTimer = useRef(null);
  const [canHover, setCanHover] = useState(false);

  useEffect(() => { setCanHover(supportsHover()); }, []);
  useEffect(() => () => { clearTimeout(closeTimer.current); }, []);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    }
    function onEsc(e) {
      if (isOpen && e.key === 'Escape') {
        setIsOpen(false);
        wrapperRef.current?.querySelector('button')?.focus();
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);

  const open = useCallback(() => {
    if (!canHover) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
    setIsOpen(true);
  }, [canHover]);

  const scheduleClose = useCallback(() => {
    if (!canHover) return;
    closeTimer.current = setTimeout(() => setIsOpen(false), 180);
  }, [canHover]);

  const toggle = useCallback(() => {
    if (!canHover) setIsOpen(v => !v);
  }, [canHover]);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <div className="relative" ref={wrapperRef} onMouseEnter={open} onMouseLeave={scheduleClose}>
      {customTrigger ? (
        <div onClick={toggle} data-testid={testId} role="button" aria-expanded={isOpen} aria-haspopup="true">
          {customTrigger}
        </div>
      ) : (
        <button
          onClick={toggle}
          onFocus={() => setIsOpen(true)}
          aria-expanded={isOpen}
          aria-haspopup="true"
          className={`px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
            isActive
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          data-testid={testId}
        >
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label && <span>{label}</span>}
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      )}

      {isOpen && <div className="absolute top-full left-0 w-full h-2" />}

      <div
        className={`absolute top-full ${customTrigger ? 'right-0' : 'left-0'} pt-1.5 z-50 transition-all duration-150 ${
          isOpen
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        role="menu"
        aria-orientation="vertical"
      >
        <div className="w-64 bg-card border border-border/70 rounded-xl shadow-xl shadow-black/20 overflow-hidden py-1">
          {renderContent({ isOpen, close })}
        </div>
      </div>
    </div>
  );
}

// Re-export helpers for use by Header
NavDropdown.Link = DropdownLink;
NavDropdown.Header = SectionHeader;
NavDropdown.Divider = SectionDivider;
